import { useCallback, useMemo, useState } from 'react'
import { Check, Gavel, X } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchReviewCampaigns, setCampaignStatus, ADS_STR as S, type CampaignRow } from './adsApi'

/**
 * A20.10 — Одобрение кампаний. Lists campaigns in `review`; approve → status
 * `active`, reject → status `rejected` (confirm dialog) via backend
 * `SetCampaignStatus` (`POST /admin/ads/campaigns/:id/status`). LIVE-ready.
 */
export function CampaignApprovalScreen() {
  const { toast } = useToast()
  const [reject, setReject] = useState<CampaignRow | null>(null)

  const columns = useMemo<AdminColumn<CampaignRow>[]>(
    () => [
      { key: 'title', header: S.title, type: 'text' },
      { key: 'advertiser', header: S.advertiser, type: 'text' },
      { key: 'budget', header: S.budget, type: 'number' },
      { key: 'cpm', header: S.cpm, type: 'number' },
    ],
    [],
  )

  const approve = useCallback(
    async (row: CampaignRow) => {
      try {
        await setCampaignStatus(row.id, 'active')
        toast(S.campaignApproved, { tone: 'success' })
      } catch {
        toast(S.moderateFailed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<CampaignRow>[]>(
    () => [
      { id: 'approve', label: S.approve, icon: <Check size={14} />, onSelect: approve },
      {
        id: 'reject',
        label: S.reject,
        icon: <X size={14} />,
        danger: true,
        onSelect: (r) => setReject(r),
      },
    ],
    [approve],
  )

  const fetcher = useCallback(fetchReviewCampaigns, [])
  return (
    <>
      <PageHeader title={S.approval} screenId="A20.10" />
      <DataTable<CampaignRow>
        screenId="A20.10"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        emptyState={{ icon: <Gavel size={28} strokeWidth={1.5} /> }}
      />

      {reject && (
        <DangerDialog
          open
          onClose={() => setReject(null)}
          screenId="A20.10"
          level={2}
          title={S.rejectTitle(reject.title)}
          description={S.rejectDesc}
          confirmLabel={S.reject}
          impact={{
            items: [
              { label: S.advertiser, value: reject.advertiser },
              { label: S.budget, value: reject.budget, emphasis: true },
            ],
            reversible: false,
          }}
          onConfirm={async () => {
            try {
              await setCampaignStatus(reject.id, 'rejected')
              toast(S.rejected, { tone: 'success' })
            } catch {
              toast(S.moderateFailed, { tone: 'error' })
            }
          }}
        />
      )}
    </>
  )
}
